<template>
  <section class="position-relative" id="file-viewer">
    <div
      class="pdf-viewer border rounded"
      :style="`height: ${height}px`"
      ref='viewer'
    ></div>

    <div class="navigation-buttons">
      <b-button variant="outline-secondary" @click="onBack" :disabled="position === 0">
        <i class="dripicons-chevron-left"></i>
      </b-button>
      <b-button variant="outline-secondary" @click="onForward" :disabled="position === flattedFiles.length - 1">
        <i class="dripicons-chevron-right"></i>
      </b-button>
    </div>

    <aside v-if="openSidebar" class="sidebar">
      <div class="border rounded shadow bg-white">
        <div class="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
          <i class="dripicons-network-1" style="font-size: 1.5rem"></i>
          <h4 class="text-center m-0">Documentos</h4>
          <span class="clickable" @click="openSidebar = false">
            <i class="mdi mdi-close" style="font-size: 1.5rem"></i>
          </span>
        </div>
        <b-list-group flush class="my-2 mx-2">
          <b-list-group-item
            v-for="(document, documentIndex) of files" :key="documentIndex"
          >
            <span
              class="clickable text-primary text-bold document-name"
              @click="onSetDocument(document); position = documentIndex"
              :class="{ 'text-underline': currentFile === document.path }"
            >
              {{ document.name }}.pdf
            </span>

            <b-list-group class="mt-3" v-if="document.attachment && document.attachments.length">
              <small class="text-muted text-uppercase mb-2">Adjuntos</small>
              <b-list-group-item
                v-for="(attachment, attachmentIndex) of document.attachments" :key="`${documentIndex} + ${attachmentIndex}`"
              >
                <span
                  class="clickable text-primary text-bold document-name"
                  @click="onSetDocument(attachment); position = documentIndex"
                  :class="{ 'text-underline': currentFile === attachment.path }"
                >
                  {{ attachment.name }}
                </span>
              </b-list-group-item>
            </b-list-group>

          </b-list-group-item>
        </b-list-group>
      </div>
    </aside>

  </section>
</template>

<script>
import WebViewer from '@pdftron/pdfjs-express-viewer'

export default {
  name: 'FileViwer',
  props: {
    files: {
      type: Array,
      default: () => []
    },
    initialPosition: {
      type: Number,
      default: 0
    },
    height: {
      type: Number,
      default: 800
    }
  },
  data () {
    return {
      position: 0,
      openSidebar: false,
      instance: null,
      currentFile: this.files[0].path
    }
  },
  computed: {
    flattedFiles () {
      const files = []
      for (const file of this.files) {
        files.push(file)
        if (file?.attachments?.length) {
          for (const attachment of file.attachments) {
            files.push(attachment)
          }
        }
      }
      return files
    }
  },
  methods: {
    onBack () {
      this.position--
      this.onSetDocument(this.flattedFiles[this.position])
    },
    onForward () {
      this.position++
      this.onSetDocument(this.flattedFiles[this.position])
    },
    getDownloadFilename (document) {
      const ext = document.path.split('.').pop()
      const filename = document.name.replace(/\s/g, '_') + '.' + ext
      return filename
    },
    onSetDocument (document) {
      const filename = this.getDownloadFilename(document)
      const path = document.path
      if (this.currentFile !== path) {
        this.currentFile = path
        this.instance.UI.loadDocument(path, {
          filename: filename
        })
      }
    }
  },
  watch: {
    // position (val) {
    //   if (this.instance) {
    //     this.onSetDocument(this.flattedFiles[val].path)
    //   }
    // },
    initialPosition (val) {
      if (this.flattedFiles[val]) {
        this.position = val
      }
    }
  },
  mounted () {
    if (this.initialPosition) {
      this.position = this.initialPosition
    }

    // Actualizo la version de pdfjs-express-viewer en base a este issue
    // https://pdfjs.community/t/pdf-viewer-react-sample-has-error/1927
    WebViewer({
      path: '/pdfjsexpress',
      initialDoc: this.flattedFiles[this.initialPosition].path,
      filename: this.getDownloadFilename(this.flattedFiles[this.initialPosition]),
      licenseKey: process.env?.VUE_APP_PDFTRON_LICENSE || null
    }, this.$refs.viewer)
      .then((instance) => {
        // Ajusto la toolbar, no se ve la forma mas linda de hacerlo
        instance.UI.setHeaderItems((header) => {
          let items = header.getItems()
          // console.log('items', items)
          const left = [{
            type: 'actionButton',
            title: 'component.leftPanel',
            img: 'icon-header-sidebar-line',
            onClick: () => {
              this.openSidebar = !this.openSidebar
            }
          }]
          const zoomControls = items.at(3)
          zoomControls.hiddenOnMobileDevice = false

          const right = [
            { type: 'spacer' },
            items.at(2), // view control
            zoomControls,
            items.at(9), // search control
            items.at(11) // settings control
          ]
          items = [...left, ...right]
          header.update(items)
        })
        instance.UI.setLanguage('es')

        instance.UI.useEmbeddedPrint(true)

        this.instance = instance
        const { Core } = instance

        // El documento terminó de cargar
        // Core.documentViewer.addEventListener('documentLoaded', () => {
        //   console.log('document loaded')
        // })

        // Deshabilito las anotaciones, no tenemos forma de guardarlas por el momento
        Core.annotationManager.enableReadOnlyMode()

        // Horrible forma provisoria para ocultar el "Firme aquí"
        Core.Annotations.SignatureWidgetAnnotation.prototype.createSignHereElement = () => {
          const div = document.createElement('div')
          return div
        }
      })
  },
  beforeDestroy () {
    if (this.$refs.viewer) {
      this.$refs.viewer.remove()
    }
    if (this.instance) {
      this.instance.dispose()
    }
  }
}
</script>

<style lang="sass">
  #file-viewer
    .pdf-viewer
      box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px !important

    .document-name
      word-break: break-all

    .navigation-buttons
      position: absolute
      top: 50%
      transform: translateY(-50%)
      width: 100%
      display: flex
      justify-content: space-between
      padding: 0 1rem
      > button > i
        display: block
        margin-bottom: 2px

    .sidebar
      position: absolute
      top: 3rem
      left: 1rem
      background: white
      width: 300px
      .text-underline
        text-decoration: underline
</style>
